import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import i18n from '../i18n';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../firebaseConfig';
import { Link } from 'react-router-dom';
import { PieChart, Pie, Cell, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import './PublicLanding.css';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';


const ITEMS_PER_PAGE = 6;

const PublicLanding = () => {
  const [locations, setLocations] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const { t } = useTranslation();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchLocations = async () => {
      const tenantsSnap = await getDocs(query(collection(db, 'tenants'), where('public_listing', '==', true)));
      const tenantList = tenantsSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));

      const enriched = await Promise.all(
        tenantList.map(async tenant => {
          const reviewsSnap = await getDocs(query(collection(db, 'reviews'), where('coffee_shop_id', '==', tenant.id)));
          const reviews = reviewsSnap.docs.map(doc => doc.data());
          const total = reviews.length;
          const pos = reviews.filter(r => r.feeling === 'positive').length;
          const score = total > 0 ? Math.round((pos / total) * 100) : 0;
          return { ...tenant, reviews, score };
        })
      );

      const sorted = enriched.sort((a, b) => b.score - a.score);
      setLocations(sorted);
    };
    fetchLocations();
  }, []);

  const COLORS = ['#4CAF50', '#FFC107', '#F44336'];

  const filteredLocations = locations.filter(loc => loc.name.toLowerCase().includes(searchTerm));
  const totalPages = Math.ceil(filteredLocations.length / ITEMS_PER_PAGE);
  const paginated = filteredLocations.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE);

  return (
    <div className="public-landing">
        <Helmet>
  <title>Top Rated Loyalty Locations | LoyalTap</title>
  <meta name="description" content="Discover top-rated coffee shop locations with the best loyalty rewards based on real customer reviews. Find your next reward experience!" />
  <meta name="keywords" content="Loyalty rewards, Coffee shop reviews, Top Rated Locations, Customer sentiment, Reward programs" />
  <meta property="og:title" content="Top Rated Loyalty Locations" />
  <meta property="og:description" content="Explore the best coffee shops based on customer reviews and sentiment evaluation." />
  <meta property="og:type" content="website" />
  <meta property="og:url" content="https://loyaltap.flexpayz.com/public-landing" />
  <link rel="canonical" href="https://loyaltap.flexpayz.com/public-landing" />
</Helmet>
<div className="language-dropdown-wrapper" style={{ marginBottom: '1rem' }}>
  <select
    onChange={(e) => i18n.changeLanguage(e.target.value)}
    value={i18n.language}
    className="language-select"
  >
    <option value="en">🇬🇧 English</option>
    <option value="sv">🇸🇪 Svenska</option>
    <option value="ro">🇷🇴 Română</option>
  </select>
</div>
      <h1>{t('public_listing.top_rated_locations')}</h1>

      <input
        type="text"
        placeholder={t('public_listing.search_location')}
        value={searchTerm}
        onChange={(e) => {
          setSearchTerm(e.target.value.toLowerCase());
          setCurrentPage(1); // Reset to page 1 on new search
        }}
        className="search-bar"
      />
<button className="export-btn1" onClick={() => navigate(-1)}>
  ⬅️ Back
</button>
      <div className="location-list">
        {paginated.map(loc => {
          const data = [
            { name: t('positive'), value: loc.reviews.filter(r => r.feeling === 'positive').length },
            { name: t('neutral'), value: loc.reviews.filter(r => r.feeling === 'neutral').length },
            { name: t('negative'), value: loc.reviews.filter(r => r.feeling === 'negative').length }
          ];

          return (
            <div key={loc.id} className="location-card">
              <h2>{loc.name}</h2>
              <p> {loc.city || loc.address || ''}</p>
              <p> {t('public_listing.sentiment_score')}: <strong>{loc.score}%</strong></p>

              <ResponsiveContainer width="100%" height={200}>
                <PieChart>
                  <Pie data={data} dataKey="value" nameKey="name" outerRadius={70}>
                    {data.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index]} />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>

              <Link to={`/public-reviews/${loc.id}`} className="view-reviews-btn">
                {t('public_listing.view_reviews')}
              </Link>
            </div>
          );
        })}
      </div>

      {totalPages > 1 && (
        <div className="pagination-controls">
          {[...Array(totalPages)].map((_, index) => (
            <button
              key={index}
              className={currentPage === index + 1 ? 'active' : ''}
              onClick={() => setCurrentPage(index + 1)}
            >
              {index + 1}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default PublicLanding;
